import React, { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ID_KINDS, dmy, recName } from "../lib/identity";
import { useIdTemple, TemplePicker, PageHead, StatusChip } from "../components/IdentityKit";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, XCircle, CheckCheck, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const KINDS = ["duty", "vehicle", "pandit"];

export default function IdentityApprovals() {
  const { user } = useAuth();
  const { temples, templeId, setTempleId } = useIdTemple(user);
  const [tab, setTab] = useState("duty");
  const [rows, setRows] = useState({});
  const [reason, setReason] = useState({});
  const [photo, setPhoto] = useState({});
  const [busy, setBusy] = useState("");
  const tparam = user.role === "SUPER_ADMIN" ? templeId : "";

  const load = useCallback(() => {
    Promise.all(KINDS.map((k) => api.get(`/identity/${k}`, { params: { temple_id: tparam, status: "PENDING" } }).then((r) => [k, r.data])))
      .then((all) => setRows(Object.fromEntries(all))).catch((e) => toast.error(fmtErr(e)));
  }, [tparam]);
  useEffect(load, [load]);

  const decide = async (r, status) => {
    if (status === "REJECTED" && !(reason[r.id] || "").trim()) { toast.error("Type the reason for rejection first"); return; }
    setBusy(r.id);
    try {
      await api.post(`/identity/${tab}/${r.id}/status`, { status, remarks: reason[r.id] || "" });
      toast.success(`${r.pass_no} ${status === "APPROVED" ? "approved" : "rejected"}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };
  const approveAll = async () => {
    const list = rows[tab] || [];
    if (!list.length || !window.confirm(`Approve all ${list.length} pending ${ID_KINDS[tab].plural}?`)) return;
    setBusy("all");
    for (const r of list) { try { await api.post(`/identity/${tab}/${r.id}/status`, { status: "APPROVED" }); } catch (e) { toast.error(`${r.pass_no}: ${fmtErr(e)}`); } }
    setBusy(""); toast.success("Approved"); load();
  };
  const showPhoto = async (r) => {
    if (photo[r.id] !== undefined) { setPhoto((p) => { const x = { ...p }; delete x[r.id]; return x; }); return; }
    try { const full = await api.get(`/identity/${tab}/${r.id}`); setPhoto((p) => ({ ...p, [r.id]: full.data.photo || "" })); } catch (e) { toast.error(fmtErr(e)); }
  };

  const list = rows[tab] || [];
  return (
    <div data-testid="id-approvals">
      <PageHead eyebrow="Identity & Passes" title="Approvals" subtitle="Passes registered by counter staff wait here. Approved passes can be printed; rejected ones show the reason.">
        <TemplePicker user={user} temples={temples} value={templeId} onChange={setTempleId} allowAll />
      </PageHead>
      <div className="flex flex-wrap gap-2 mb-3">
        {KINDS.map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`id-appr-tab-${k}`}
            className={`h-10 px-4 rounded-lg text-sm font-semibold border ${tab === k ? "bg-maroon-700 text-white border-maroon-700" : "bg-white border-[#E5D9C5] text-maroon-800"}`}>
            {ID_KINDS[k].plural} <span className={`ml-1 px-1.5 rounded-full text-xs ${tab === k ? "bg-white/25" : "bg-amber-100 text-amber-800"}`}>{(rows[k] || []).length}</span>
          </button>
        ))}
        <div className="flex-1" />
        {list.length > 1 && <Button onClick={approveAll} disabled={busy === "all"} className="bg-green-700 hover:bg-green-800 text-white" data-testid="id-approve-all"><CheckCheck className="w-4 h-4 mr-1" /> Approve all ({list.length})</Button>}
      </div>
      <Card><CardContent className="p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="id-approvals-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <th className="py-2 pr-3">Pass No</th>{user.role === "SUPER_ADMIN" && !templeId && <th className="py-2 pr-3">Temple</th>}<th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Details</th>
            <th className="py-2 pr-3">Validity</th><th className="py-2 pr-3">By</th><th className="py-2 pr-3">Reason (for rejection)</th><th className="py-2 text-right">Decision</th>
          </tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Nothing waiting for approval</td></tr>}
            {list.map((r) => (
              <tr key={r.id} className="border-b border-[#F1EBE3] align-top">
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{r.pass_no}<br /><StatusChip s={r.status} /></td>
                {user.role === "SUPER_ADMIN" && !templeId && <td className="py-2 pr-3">{r.temple_id}</td>}
                <td className="py-2 pr-3">
                  <p className="font-semibold">{recName(r)}</p>
                  {r.vehicle_number && <p className="font-mono font-bold">{r.vehicle_number}</p>}
                  <p className="text-xs text-slate-500">{r.mobile}</p>
                  {tab !== "vehicle" && (
                    <button className="text-[11px] text-maroon-700 underline inline-flex items-center gap-1" onClick={() => showPhoto(r)}><ImageIcon className="w-3 h-3" /> {photo[r.id] !== undefined ? "Hide photo" : "Photo"}</button>
                  )}
                  {photo[r.id] !== undefined && (photo[r.id] ? <img src={photo[r.id]} alt="" className="mt-1 w-20 h-24 object-cover rounded border" /> : <p className="text-[11px] text-slate-400">No photo</p>)}
                </td>
                <td className="py-2 pr-3 text-xs text-slate-700">
                  {[r.designation, r.vehicle_type, r.department_name, r.location_name, r.city].filter(Boolean).join(" · ")}
                </td>
                <td className="py-2 pr-3 text-xs whitespace-nowrap">{dmy(r.valid_from)} – {dmy(r.valid_to)}</td>
                <td className="py-2 pr-3 text-xs">{r.created_by_name}<br /><span className="text-slate-400">{dmy(r.created_at)}</span></td>
                <td className="py-2 pr-3"><Input value={reason[r.id] || ""} onChange={(e) => setReason((x) => ({ ...x, [r.id]: e.target.value }))} placeholder="Optional for approval" className="h-9 min-w-[160px]" /></td>
                <td className="py-2 text-right whitespace-nowrap">
                  <Button size="sm" disabled={busy === r.id} onClick={() => decide(r, "APPROVED")} className="bg-green-700 hover:bg-green-800 text-white mr-1" data-testid={`id-approve-${r.pass_no}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => decide(r, "REJECTED")} className="border-red-300 text-red-700" data-testid={`id-reject-${r.pass_no}`}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
